import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import axios from 'axios';

// Componentes, Estilos y Configuración
import DiscoverButton from '../../atoms/DiscoverButton/DiscoverButton';
import OtherRoomsSkeleton from './OtherRoomsSkeleton'; // <--- Tu Skeleton
import './OtherRoomsSection.css';
import { routeMap } from '../../../config/routes'; // Mapa de rutas

interface OtherRoomsSectionProps {
  currentRoomId: string; // Recibe el SLUG de la habitación actual
}

// Tipo mínimo necesario para esta sección
interface RoomSummary {
  id: number;
  title: string;
  slug: string;
  images: { url: string }[];
}

const OtherRoomsSection: React.FC<OtherRoomsSectionProps> = ({ currentRoomId }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language.split('-')[0] as 'es' | 'en';

  const [rooms, setRooms] = useState<RoomSummary[]>([]);
  const [loading, setLoading] = useState(true);

  // Variable de entorno compatible con CRA
  const API_URL = process.env.REACT_APP_API_URL || 'https://admin.villasflamingos.com/api';

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        setLoading(true);
        // Petición a la API incluyendo el idioma en headers
        const response = await axios.get(`${API_URL}/rooms`, {
          headers: { 'Accept-Language': currentLang }
        });

        // Laravel Resources devuelve la info dentro de "data"
        setRooms(response.data.data);
      } catch (error) {
        console.error("Error cargando otras habitaciones:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRooms();
  }, [currentLang, API_URL]);

  // Filtramos para NO mostrar la habitación que el usuario ya está viendo
  const roomsToShow = rooms.filter(room => room.slug !== currentRoomId);

  // Helper para construir el enlace correcto según idioma
  const getRoomLink = (slug: string) => {
    // Busca el patrón en routeMap (ej: "habitaciones/:slug")
    const routePattern = routeMap.roomDetail[currentLang];
    const path = routePattern.replace(':slug', slug);
    return `/${currentLang}/${path}`;
  };

  // Si está cargando, mostramos el Skeleton en lugar de nada
  if (loading) return <OtherRoomsSkeleton />;

  return (
    <section className="other-rooms-section">
      <h2 className="section-title-other-rooms">
        <Trans i18nKey="otherRooms:title" />
      </h2>

      <div className="other-rooms-grid">
        {roomsToShow.map((room) => {

          const localizedLink = getRoomLink(room.slug);

          // Usamos la primera imagen como thumbnail
          const thumbnail = room.images && room.images.length > 0
            ? room.images[0].url
            : '';

          return (
            <div key={room.id} className="room-card">
              <Link to={localizedLink}>
                <div className="room-card-image-container">
                  {thumbnail ? (
                    <img
                      src={thumbnail}
                      alt={room.title}
                      className="room-card-image"
                      loading="lazy"
                    />
                  ) : (
                    <div className="bg-gray-200 h-64 w-full flex items-center justify-center text-gray-400">
                      No Image
                    </div>
                  )}
                </div>
              </Link>

              <h3 className="room-card-name">{room.title}</h3>

              <DiscoverButton link={localizedLink} />
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default OtherRoomsSection;